import React, { useEffect, useState } from 'react';
import theme from 'theme';
import { Alert, Grid, PageHeader, Row, Stack } from 'library/ui';
import { useMediaQuery } from '@mui/material';
import { useClient } from 'shared';
import { PermissionType, useUserContext } from 'UserContext';

import { PostAJob } from './components/PostAJob';
import { ReferralBanner } from './components/ReferralBanner';
import { Notifications } from './components/Notifications';
import { OnboardProgress } from './components/OnboardProgress';
import { Reviews } from './components/Reviews';
import { Timesheets } from './components/Timesheets';
import { WelcomeModal } from './components/WelcomeModal';
import { ELCWelcomeModal } from './components/ELCWelcomeModal';

import type { DashboardSummary } from './types';
import { ActionNeededWorkers } from './components/ActionNeededWorkers';
import { ApplicantsToReview } from './components/ApplicantsToReview';
import { PaymentStatus } from './components/PaymentStatus';
import { ScheduleACall } from './components/ScheduleACall';
import HowItWorks from './components/HowItWorks';
import AvailableTeachers from './components/AvailableTeachers';
import { TeacherShowcase } from './components/TeacherShowcase';
import PayMethodMandates from './components/PayMethodMandates';
import { ELCResourceCard } from './components/ELCResourceCard';
import { FeeFreeShiftsCard } from './components/FeeFreeShiftsCard';
import { ELCEligibilityModal } from './components/ELCEligibilityModal';
import { ActionButtonRow } from './components/ActionButtonRow';
import { useBlockedCounty } from 'parent-portal/shared/BlockedCounty/useBlockedCounty';
import { BlockedCountyNotice } from 'parent-portal/shared/BlockedCounty/BlockedCountyNotice';
import { useFloridaClearinghouseNotice } from 'parent-portal/shared/FloridaClearinghouse/useFloridaClearinghouseNotice';
import { FloridaClearinghouseNotice } from 'parent-portal/shared/FloridaClearinghouse/FloridaClearinghouseNotice';
import { useFloridaClearinghousePolicy } from 'parent-portal/shared/FloridaClearinghouse/useFloridaClearinghousePolicy';
import { FloridaClearinghousePolicyModal } from 'parent-portal/shared/FloridaClearinghouse/FloridaClearinghousePolicyModal';

export default function Dashboard() {
    const { user, hasPermission } = useUserContext();
    const { isInBlockedCounty } = useBlockedCounty();
    const { showFloridaClearinghouseNotice } = useFloridaClearinghouseNotice();
    const { showPolicyModal, agreeToPolicy } = useFloridaClearinghousePolicy();
    const [showRefer, setShowRefer] = useState<boolean>(true);
    const [showWelcomeModal, setShowWelcomeModal] = useState<boolean>(!!user?.welcome_attribute);
    const { data, loading, error, refresh } = useClient<DashboardSummary>({
        url: 'api/dashboard/summary/',
    });

    const small = useMediaQuery(theme.breakpoints.down('lg'));

    const elcPartner = data?.sub_pool;
    const potentialSubPool = data?.potential_sub_pool;
    const elcWelcomeAttribute = user?.sub_pool_welcome_attribute;
    const [showELCModal, setShowELCModal] = useState<boolean>(!!elcWelcomeAttribute);
    const welcomeModalOpen = elcPartner || potentialSubPool ? showELCModal : showWelcomeModal;

    useEffect(() => setShowWelcomeModal(!!user?.welcome_attribute), [user?.welcome_attribute]);
    useEffect(() => setShowELCModal(!!elcWelcomeAttribute), [elcWelcomeAttribute?.id]);

    const isManualPayer = user?.businesses_active?.some((x) => Boolean(x.billing_cadence));
    const canPostJobs = hasPermission(PermissionType.PostJob);
    const canManagePayments = hasPermission(PermissionType.ManagePayments);
    const paymentSetupNeeded = !!data && data.payment?.status !== 'complete';
    const showOnboarding = !!data && !data.has_posted_jobs;
    const canScheduleCall = !!data && !data.has_posted_jobs && !small;

    const teacherList = user?.is_new_market ? <AvailableTeachers /> : <TeacherShowcase />;
    const showHowItWorks = !!data && !data.has_paid_worker;

    return (
        <Stack gap="lg" fullWidth>
            {data?.show_refer && showRefer && <ReferralBanner small={small} setShowRefer={setShowRefer} />}
            <Row justify="between">
                <PageHeader
                    id="dashboard-welcome-title"
                    title={`${user?.first_name ? `Hi ${user.first_name}!` : 'Hi!'}`}
                />
                {canScheduleCall && <ScheduleACall />}
            </Row>
            {data && !data.has_posted_jobs && small ? <ScheduleACall /> : null}
            {isInBlockedCounty && (
                <Alert variant="warning">
                    <BlockedCountyNotice />
                </Alert>
            )}
            {showFloridaClearinghouseNotice && data?.has_posted_jobs && (
                <Alert variant="warning">
                    <FloridaClearinghouseNotice />
                </Alert>
            )}
            {elcPartner && <ELCResourceCard partner={elcPartner} />}
            <FeeFreeShiftsCard allowances={data?.fee_free_shifts ?? []} poolName={elcPartner?.name ?? null} />
            {!isManualPayer &&
            data?.payment.business_payment_status &&
            ['BLOCKED', 'WARNING'].includes(data?.payment.business_payment_status) ? (
                <PaymentStatus status={data?.payment.business_payment_status} />
            ) : null}
            {data?.payment?.pay_mandates_needed?.length ? (
                <PayMethodMandates neededMandates={data.payment.pay_mandates_needed} refresh={refresh} />
            ) : null}
            <ActionButtonRow
                canPostJobs={canPostJobs}
                canManagePayments={canManagePayments}
                paymentSetupNeeded={paymentSetupNeeded}
                awaitingUpdateCount={data?.awaiting_clearinghouse_update_count ?? 0}
            />
            {data && (
                <>
                    {showOnboarding ? (
                        <OnboardProgress data={data} refresh={refresh} />
                    ) : (
                        <Grid template="3fr 2fr" collapseAt="lg" gap="md" align="start">
                            <Stack gap="md">
                                <ApplicantsToReview />
                                <PostAJob data={data} loading={loading} error={!!error} refresh={refresh} />
                                <ActionNeededWorkers
                                    loading={loading}
                                    workers={data.action_required_workers}
                                    refresh={refresh}
                                />
                                <Timesheets dashboardSummary={data} loading={loading} refresh={refresh} />
                                {teacherList}
                            </Stack>
                            <Stack gap="md">
                                <Notifications data={data} />
                                <Reviews loading={loading} workers={data.reviews} />
                            </Stack>
                        </Grid>
                    )}
                    {showOnboarding && teacherList}
                    {showHowItWorks && <HowItWorks />}
                </>
            )}
            {data &&
                !potentialSubPool &&
                (elcPartner ? (
                    <ELCWelcomeModal partner={elcPartner} open={showELCModal} onClose={() => setShowELCModal(false)} />
                ) : (
                    <WelcomeModal open={showWelcomeModal} onClose={() => setShowWelcomeModal(false)} />
                ))}
            {potentialSubPool && (
                <ELCEligibilityModal
                    partner={potentialSubPool}
                    open={showELCModal}
                    onClose={() => setShowELCModal(false)}
                />
            )}
            {data && showPolicyModal && !welcomeModalOpen && (
                <FloridaClearinghousePolicyModal onAgree={agreeToPolicy} />
            )}
        </Stack>
    );
}
